import { corejs, regeneratorRuntime } from '@sparky/polyfills'
import lodash from 'lodash'
import React from 'react'
import ReactJsxRuntime from 'react/jsx-runtime'
import ReactDOM from 'react-dom' // too big, but needed for almost all chunks
// import * as ReactRedux from 'react-redux' // moved to defaults, use react-dom batchupdates
import * as ReactIs from 'react-is'
import * as ReactIntl from 'react-intl'
import * as ReactRouter from 'react-router'
import * as ReactRouterDOM from 'react-router-dom'
import PropTypes from 'prop-types'
import * as history from 'history'
import i18next from 'i18next'
import i18nextICU from 'i18next-icu'
import { fetch } from './fetch'
import importFn from './moduleImport'
import patchReactDOM from '../patches'
import type { LoDashStatic } from 'lodash'

export type LibsObj = Record<string, any>
export type Libs = Readonly<LibsObj & { __MICRO_APPS__: Record<string, any> }>

declare global {
  interface Window {
    __SPARKY_LIBS__: Libs
    _: LoDashStatic
  }
}

// for default module just in case
Object.defineProperties(lodash, { lodash: { value: lodash }, default: { value: lodash } })

for (const def of [corejs, i18next, i18nextICU]) {
  if (!('default' in def)) {
    Object.defineProperties(def, { default: { value: def } })
  }
}

export const customElements = window.customElements

export const moduleImport = importFn as <T = any>(url: string) => Promise<T>

const EMPTY = { default: {} }
const libs = {
  EMPTY,
  corejs,
  coreJsStable: corejs,
  customElements,
  React,
  ReactDOM,
  ReactJsxRuntime,
  ReactIs,
  ReactIntl,
  ReactRouter,
  ReactRouterDOM,
  PropTypes,
  lodash,
  history,
  fetch,
  i18next,
  i18nextICU,
  regeneratorRuntime,
  moduleImport,
} as LibsObj

patchReactDOM(ReactDOM)

export const microApps = {}

const exported = Object.freeze(Object.defineProperties(Object.create(libs), {
  __MICRO_APPS__: { value: microApps },
})) as Libs

if (!window.__SPARKY_LIBS__) {
  Object.defineProperties(window, {
    '__SPARKY_LIBS__': {
      value: exported
    },
    _: {
      value: lodash,
    },
  })
}

export const addLibs = (more: LibsObj): LibsObj => Object.assign(libs, more)

/* below are deprecated */

const LIB_PKG_IMPORTS = {} as Record<string, () => Promise<any>>
export const addPkgs = (obj: LibsObj) => {
  lodash.each(obj, (fn, pkg) => {
    LIB_PKG_IMPORTS[pkg] = () => {
      const promise = fn().then((exports: LibsObj) => {
        addLibs(exports)
        return exports
      })
      LIB_PKG_IMPORTS[pkg] = () => promise // ensure addLibs once
      return promise
    }
  })
}

export const loadLibPkgFn = (pkg: string, name?: string) => {
  const libFn = LIB_PKG_IMPORTS[pkg]
  if (!libFn) {
    return () => undefined
  }
  if (!name) {
    return libFn
  }
  return () => (libs.hasOwnProperty(name) ? libs[name] : libFn().then((exports: LibsObj) => exports[name]))
}

export type LibFnMap = Record<string, Function>
const libsMap = {
  // panwds already bundled in by default now
  // 'panwds': loadLibPkgFn('panwds'),
  // 'date-fns': loadLibPkgFn('panwds', 'dateFns'),
  // 'react-hook-form': loadLibPkgFn('panwds', 'reactHookForm'),
  // '@panwds/tailwindcss': loadLibPkgFn('panwds', 'tailwindcss'),
  // '@panwds/react-ui': loadLibPkgFn('panwds', 'PanWDSReactUI'),
  // '@panwds/react-table': loadLibPkgFn('panwds', 'PanWDSReactTable'),
  // '@panwds/react-filters': loadLibPkgFn('panwds', 'PanWDSReactFilters'),
  // '@panwds/react-form': loadLibPkgFn('panwds', 'PanWDSReactForm'),
} as LibFnMap
export const addLibsMap = (obj: LibFnMap) => Object.assign(libsMap, obj)

let AGGRID_LICENSE_KEY: string | undefined
export const registerAGGridLicenseKey = (key: string) => {
  if (libs.agGridEnterprise?.LicenseManager) {
    libs.agGridEnterprise.LicenseManager.setLicenseKey(key)
  }
  else {
    AGGRID_LICENSE_KEY = key
  }
}
export const applyAGGridLicenseKey = (modules: LibsObj) => {
  if (AGGRID_LICENSE_KEY) {
    modules.agGridEnterprise?.LicenseManager?.setLicenseKey(AGGRID_LICENSE_KEY)
    AGGRID_LICENSE_KEY = undefined
  }
  return modules
}

export type ImportLibsResults = any[] & LibsObj
export const importLibs = (names: string[]) => Promise.all(names.reduce((libs, name) => {
  const lib = libsMap[name]?.()
  libs.push(lib)
  libs[name] = lib
  return libs
}, [] as ImportLibsResults))

export { exported as libs, fetch }

export default exported
